/*
 * Copyright (C) 2026 Motorola Solutions, Inc.
 * All Rights Reserved.
 * Motorola Solutions Confidential Restricted.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Button from '@msi/cobalt-react/button';
import Icon from '@msi/cobalt-react/icon';
import { Dropdown, DropdownItem } from '@msi/cobalt-react/dropdown';
import DeleteApiModal from '../DeleteApi/DeleteApi';
import { Api } from '../../types/apiData';

/** Estimated height (px) of the dropdown menu, used to decide top-end vs bottom-end placement */
const DROPDOWN_MENU_HEIGHT = 260;

interface RowActionsMenuProps {
  api: Api;
  canEdit: boolean;
  onEdit: (api: Api) => void;
  onGetUpdates: (api: Api) => void;
  onUnsubscribe: (api: Api) => void;
  isSubscribed: boolean;
  isUpdatesActionDisabled?: boolean;
  onDelete: (id?: string, e?: React.MouseEvent) => void;
  closeSignal: number;
}

export const RowActionsMenu: React.FC<RowActionsMenuProps> = ({
  api,
  canEdit,
  onEdit,
  onGetUpdates,
  onUnsubscribe,
  isSubscribed,
  isUpdatesActionDisabled = false,
  onDelete,
  closeSignal,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'bottom-end' | 'top-end'>('bottom-end');
  const deleteTriggerRef = useRef<((e?: React.MouseEvent) => void) | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setShowMenu(false);
  }, [closeSignal]);

  useEffect(() => {
    if (!showMenu) return;

    const close = () => setShowMenu(false);

    window.addEventListener('scroll', close, true);
    document.addEventListener('wheel', close, { passive: true });

    return () => {
      window.removeEventListener('scroll', close, true);
      document.removeEventListener('wheel', close);
    };
  }, [showMenu]);

  const handleAnchorClick = useCallback(() => {
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setDropdownPosition(spaceBelow < DROPDOWN_MENU_HEIGHT ? 'top-end' : 'bottom-end');
    }
  }, []);

  const handleToggleOpen = useCallback((open: boolean) => {
    setShowMenu(open);
  }, []);

  const handleMenuItemClick = (
    event: React.MouseEvent,
    action?: () => void,
    disabled?: boolean
  ) => {
    event.stopPropagation();
    if (disabled || !action) {
      return;
    }
    action();
  };

  const handleDeleteClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!canEdit) {
      return;
    }
    deleteTriggerRef.current?.(event);
  };

  const menuOptions: Array<{
    key: string;
    label: string;
    action?: () => void;
    disabled?: boolean;
  }> = [
    {
      key: 'edit',
      label: 'Edit',
      action: () => onEdit(api),
      disabled: !canEdit,
    },
    {
      key: 'duplicate',
      label: 'Duplicate',
      disabled: true,
    },
    {
      key: 'getUpdates',
      label: isSubscribed ? 'Unsubscribe' : 'Get updates',
      action: () => (isSubscribed ? onUnsubscribe(api) : onGetUpdates(api)),
      disabled: isUpdatesActionDisabled,
    },
  ];

  return (
    <div className="row-actions" ref={anchorRef} onClick={(e) => e.stopPropagation()}>
      {canEdit && (
        <DeleteApiModal
          apiId={api.id || ''}
          apiName={api.fullApiName}
          onDelete={onDelete}
          trigger={({ onClick }: { onClick: any }) => {
            deleteTriggerRef.current = onClick;
            return null;
          }}
        />
      )}
      <Dropdown
        anchor={() => (
          <Button
            variant="text"
            className="icon-button"
            aria-label="Row actions"
            type="button"
            onClick={handleAnchorClick}
          >
            <Icon name="ic_more" size={24} color="primary" />
          </Button>
        )}
        className="row-actions-dropdown"
        position={dropdownPosition}
        isOpen={showMenu}
        onToggleOpen={handleToggleOpen}
        closeOnClick
      >
        {menuOptions.map((option) => (
          <DropdownItem
            key={option.key}
            disabled={option.disabled}
            className={`row-action-item${option.disabled ? ' row-action-item-disabled' : ''}`}
            onClick={(event: React.MouseEvent) =>
              handleMenuItemClick(event, option.action, option.disabled)
            }
          >
            {option.label}
          </DropdownItem>
        ))}
        <DropdownItem
          disabled={!canEdit}
          className={`row-action-item destructive${!canEdit ? ' row-action-item-disabled' : ''}`}
          onClick={handleDeleteClick}
        >
          Delete
        </DropdownItem>
      </Dropdown>
    </div>
  );
};
